import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { useAuthContext } from '../contexts/AuthContext';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Centered modal dialog for sign-in / sign-up.
 * Shows Google OAuth button, a divider, and an email + password form.
 */
export const AuthDialog: React.FC<AuthDialogProps> = ({ isOpen, onClose }) => {
  const auth = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [flow, setFlow] = useState<'signIn' | 'signUp'>('signIn');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFlow('signIn');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Close the dialog and redirect once the user becomes authenticated
  useEffect(() => {
    if (isOpen && auth?.isAuthenticated) {
      onClose();
      // Redirect to dashboard if currently on landing or root page
      if (location.pathname === '/landing' || location.pathname === '/') {
        navigate('/budgets/current', { replace: true });
      }
    }
  }, [isOpen, auth?.isAuthenticated, onClose, navigate, location.pathname]);

  if (auth == null) return null;

  const handleGoogleSignIn = () => {
    setError(null);
    void auth.signIn('google');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    const formData = new FormData(e.currentTarget);
    formData.set('flow', flow);
    try {
      await auth.signIn('password', formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} initialFocus={emailRef} className="relative z-50">
      <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50" aria-hidden="true" />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center px-4 py-8">
          <Dialog.Panel className="w-full max-w-xs rounded-lg bg-white p-5 shadow-xl dark:bg-gray-800">
            <Dialog.Title className="mb-1 text-center text-lg font-semibold text-gray-900 dark:text-white">
              {flow === 'signUp' ? t('auth.signUp', 'Sign up') : t('auth.signIn', 'Sign in')}
            </Dialog.Title>
            <p className="mb-5 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('auth.signInHint', 'Sign in to sync across devices')}
            </p>

            {/* Google OAuth */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
            >
              <GoogleIcon />
              {t('auth.signInWithGoogle', 'Sign in with Google')}
            </button>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
              <span className="text-xs uppercase text-gray-400 dark:text-gray-500">
                {t('auth.or', 'or')}
              </span>
              <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
            </div>

            {/* Email + password form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="auth-email" className="sr-only">
                  {t('auth.email', 'Email')}
                </label>
                <input
                  ref={emailRef}
                  id="auth-email"
                  name="email"
                  type="email"
                  placeholder={t('auth.email', 'Email')}
                  required
                  autoComplete="email"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
                />
              </div>
              <div>
                <label htmlFor="auth-password" className="sr-only">
                  {t('auth.password', 'Password')}
                </label>
                <input
                  id="auth-password"
                  name="password"
                  type="password"
                  placeholder={t('auth.password', 'Password')}
                  required
                  minLength={8}
                  autoComplete={flow === 'signUp' ? 'new-password' : 'current-password'}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-500"
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                {isSubmitting
                  ? t('auth.loading', 'Loading…')
                  : flow === 'signUp'
                    ? t('auth.signUp', 'Sign up')
                    : t('auth.signIn', 'Sign in')}
              </button>
            </form>

            {/* Toggle sign-in / sign-up */}
            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              <button
                type="button"
                onClick={() => {
                  setFlow((f) => (f === 'signIn' ? 'signUp' : 'signIn'));
                  setError(null);
                }}
                className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                {flow === 'signIn'
                  ? t('auth.signUpInstead', 'Sign up instead')
                  : t('auth.signInInstead', 'Sign in instead')}
              </button>
            </p>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

/** Inline Google "G" logo SVG */
function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
